import { fetchWithRetry } from '@tunein/web-utils';
import { v4 as uuidV4 } from 'uuid';
import { debug } from '../../../utils/logger';
import CompanionAd from '../CompanionAd';
import TrackingEvent from '../TrackingEvent';
import { SSAIManager } from '../types';
const MEDIA_TAILOR_TO_SSAI_EVENT_ALLOWLIST_MAP = {
    firstQuartile: 'adfirstquartile',
    midpoint: 'admidpoint',
    thirdQuartile: 'adthirdquartile',
};
export class MediaTailorManager extends SSAIManager {
    #streamUrl;
    #trackingUrl;
    #requestParams;
    #currentAd;
    #adAvails = new Map();
    #element;
    #lastMuted = false;
    #adRequestId = '';
    setRequestParams(params) {
        this.#requestParams = params;
    }
    getStreamUrl() {
        return this.#streamUrl;
    }
    async requestStream(url) {
        try {
            const urlObj = new URL(url);
            const { manifestUrl, trackingUrl } = await fetchWithRetry(urlObj.toString(), {
                post: {
                    adsParams: this.#requestParams,
                },
            });
            this.#streamUrl = `${urlObj.origin}${manifestUrl}`;
            this.#trackingUrl = `${urlObj.origin}${trackingUrl}`;
        }
        catch (error) {
            this.emit('error', { context: 'manifest', error });
        }
    }
    setElement(element) {
        this.#element = element;
        this.#lastMuted = element.muted;
    }
    updateBeaconListeners(element) {
        if (!this.#trackingUrl) {
            return;
        }
        this.setElement(element);
        this.#initializeAdEvents(element);
    }
    processMetadata(data) {
        if (!this.#element) {
            return;
        }
        const dateRangeTag = data?.tags?.find((tag) => tag.key === 'EXT-X-DATERANGE');
        const dateRangeData = dateRangeTag?.value;
        if (dateRangeData?.includes('X-TUNEIN-AD-EVENT="START"') ||
            dateRangeData?.includes('X-TUNEIN-AD-EVENT="END"')) {
            this.updateBeaconListeners(this.#element);
        }
    }
    destroy() {
        this.#element = undefined;
        this.#currentAd = null;
        this.#streamUrl = '';
        this.#trackingUrl = '';
        this.#adRequestId = '';
        this.#clearAllAvails();
    }
    #fetchAvails = async () => {
        if (!this.#trackingUrl) {
            return;
        }
        try {
            const adData = await fetchWithRetry(this.#trackingUrl);
            return adData.avails;
        }
        catch (error) {
            this.emit('error', { context: 'tracking', error, fatal: false });
        }
    };
    #createAdEvent = (eventName, eventId, ad) => {
        return {
            ad,
            eventId,
            eventType: eventName,
            adType: 'instream',
            requestId: this.#adRequestId,
        };
    };
    #setTrackingEvents(availId, ad, element) {
        const { currentTime } = element;
        const elementEvents = this.#reduceElementEvents(ad, currentTime);
        const timedEvents = this.#filterTimedEvents(ad, currentTime);
        const avail = this.#adAvails.get(availId);
        timedEvents.forEach(({ eventId, eventType, beaconUrls, startTimeInSeconds, }) => {
            const ssaiEventName = MEDIA_TAILOR_TO_SSAI_EVENT_ALLOWLIST_MAP[eventType];
            let onTrigger = (eventName) => {
                if (ssaiEventName) {
                    this.emit(ssaiEventName, this.#createAdEvent(ssaiEventName, eventId, ad));
                }
                debug(`[SSAI] ${eventName}`);
            };
            if (eventType === 'start') {
                onTrigger = (eventName) => {
                    if (this.#currentAd === null) {
                        this.emit('adbreakstart', this.#createAdEvent('adbreakstart', eventId, ad));
                    }
                    this.emit('adstart', this.#createAdEvent('adstart', eventId, ad));
                    debug(`[SSAI] ${eventName}`);
                    this.#currentAd = ad;
                    ad.companionAds?.forEach((companionData) => {
                        const { attributes, companionClickThrough, companionClickTracking, htmlResource, staticResource, trackingEvents, } = companionData;
                        const companionAd = new CompanionAd();
                        companionAd.display({
                            companionClickThrough,
                            companionClickTracking,
                            htmlResource,
                            staticResource,
                            trackingEvents,
                            height: attributes?.height,
                            width: attributes?.width,
                        });
                    });
                };
            }
            if (eventType === 'complete') {
                onTrigger = (eventName) => {
                    this.emit('adcomplete', this.#createAdEvent('adcomplete', eventId, ad));
                    debug(`[SSAI] ${eventName}`);
                    this.#currentAd = null;
                    this.#adRequestId = '';
                };
            }
            avail?.add(new TrackingEvent({
                name: eventType,
                beaconUrls,
                timeout: (startTimeInSeconds - currentTime) * 1000,
                onTrigger,
            }));
        });
        element.addEventListener('pause', () => {
            this.#clearAllAvails();
            if (this.#currentAd?.adId === ad.adId) {
                debug('[SSAI] pause');
                avail?.add(new TrackingEvent({
                    name: 'pause',
                    beaconUrls: elementEvents.pause.beaconUrls,
                }));
            }
        });
        element.addEventListener('play', () => {
            if (this.#currentAd?.adId === ad.adId) {
                debug('[SSAI] resume');
                avail?.add(new TrackingEvent({
                    name: 'resume',
                    beaconUrls: elementEvents.resume.beaconUrls,
                }));
            }
        });
        element.addEventListener('volumechange', () => {
            if (this.#currentAd?.adId === ad.adId) {
                if (element.muted !== this.#lastMuted) {
                    const eventName = element.muted ? 'mute' : 'unmute';
                    debug(`[SSAI] ${eventName}`);
                    new TrackingEvent({
                        name: eventName,
                        beaconUrls: elementEvents.mute.beaconUrls,
                    });
                }
            }
        });
    }
    #initializeAdEvents = async (element) => {
        const avails = await this.#fetchAvails();
        if (!avails?.[0]?.ads || avails?.[0]?.ads?.length === 0) {
            this.#adRequestId = '';
            this.emit('info', {
                details: {
                    name: 'trackingUrlEmptyResponse',
                },
            });
        }
        this.#adRequestId = uuidV4();
        avails?.forEach(({ availId, ads }) => {
            this.#clearAvail(availId);
            if (!this.#adAvails.has(availId)) {
                this.#adAvails.set(availId, new Set());
            }
            debug(`Registering ad tracking events for avail: ${availId}`, ads);
            element.addEventListener('emptied', () => {
                this.#clearAllAvails();
            });
            ads?.forEach((ad) => {
                this.#setTrackingEvents(availId, ad, element);
            });
        });
    };
    #reduceElementEvents = (ad, currentTime) => {
        const ELEMENT_EVENTS = [
            'pause',
            'resume',
            'mute',
            'unmute',
            'acceptInvitationLinear',
            'closeLinear',
            'rewind',
        ];
        return ad?.trackingEvents?.reduce((obj, event) => {
            if (event.startTimeInSeconds - currentTime > 0 &&
                ELEMENT_EVENTS.includes(event.eventType)) {
                obj[event.eventType] = event;
            }
            return obj;
        }, {});
    };
    #filterTimedEvents = (ad, currentTime) => {
        const TIMED_EVENTS = [
            'start',
            'firstQuartile',
            'midpoint',
            'thirdQuartile',
            'complete',
            'impression',
            'creativeView',
        ];
        return ad?.trackingEvents?.filter((event) => {
            return (event.startTimeInSeconds - currentTime > 0 &&
                TIMED_EVENTS.includes(event.eventType));
        });
    };
    #clearAvail = (availId) => {
        const avail = this.#adAvails.get(availId);
        avail?.forEach((trackingEvent) => {
            trackingEvent.destroy();
        });
        avail?.clear();
    };
    #clearAllAvails = () => {
        for (const availId of this.#adAvails.keys()) {
            this.#clearAvail(availId);
        }
    };
}
